/**
 * @file Слой 2 - Образ Начала
 * @module membrane/src/layers/Layer2Image
 * 
 * Второй слой мембраны - визуальный образ будущего.
 * Показывает, но не объясняет. Передаёт вибрацию через образ.
 */

import { motion } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useState, useEffect } from 'react';

/**
 * Компонент Layer2Image - образ Начала
 * 
 * Отображает визуальный образ (изображение/видео/анимацию) с коротким текстом.
 * Плавный переход из слоя 1. Позволяет почувствовать будущее, не объясняя его.
 */
export default function Layer2Image({ onProceed, onReturn, state: _state }: BaseLayerProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Устанавливаем imageLoaded после монтирования
  useEffect(() => {
    // Симулируем загрузку образа
    const timer = setTimeout(() => {
      setImageLoaded(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full"
    >
      {/* Фон с градиентом */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-membrane-light to-white" />

      {/* Визуальный образ */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: imageLoaded ? 1 : 0 }}
          transition={{ duration: 3, ease: 'easeOut' }}
          className="relative max-w-4xl w-full"
        >
          {/* Placeholder для образа - можно заменить на реальное изображение */}
          <div className="aspect-video bg-gradient-to-br from-membrane-depth/5 to-membrane-depth/10 rounded-lg overflow-hidden">
            <div className="w-full h-full flex items-center justify-center">
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ 
                  duration: 6, 
                  repeat: Infinity, 
                  ease: 'easeInOut' 
                }}
                className="w-32 h-32 rounded-full border border-membrane-depth/20"
              />
            </div>
          </div>

          {/* Текст-приглашение */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2, delay: 2 }}
            className="mt-12 text-center"
          >
            <p className="text-lg md:text-xl font-light text-membrane-depth/70 leading-relaxed max-w-xl mx-auto">
              Это не проект и не цель.<br />
              Это состояние, которое уже существует.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Возможность вернуться (неявная) */}
      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          whileHover={{ opacity: 0.5 }}
          onClick={onReturn}
          className="absolute top-8 left-8 text-membrane-depth/40 hover:text-membrane-depth/60 transition-opacity"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}

      {/* Скрытый триггер для следующего слоя - активируется через внимание */}
      {imageLoaded && onProceed && (
        <div
          className="absolute inset-0 cursor-default"
          onMouseMove={(e) => {
            // Активируем только один раз
            if (!e.currentTarget.dataset.activated) {
              e.currentTarget.dataset.activated = 'true';
              setTimeout(() => onProceed?.(), 3000);
            }
          }}
        />
      )}
    </motion.div>
  );
}

